<template>
    <div class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
        <div class="flex items-center px-6 py-3 border-b border-gray-300">
            <h3 class="text-lg text-gray-700 font-bold">Feedback Reports</h3>
        </div>

        <div v-if="!reports.length" class="flex flex-col items-center justify-center space-y-1 p-6">
            <UIcon name="i-ph-file-x-duotone" class="size-5 text-gray-500" />
            <span class="text-[13px] italic text-gray-500">No feedback reports.</span>
        </div>

        <div v-else class="space-y-4 px-6 py-5">
            <div v-for="report in reports" :key="report.id" class="relative bg-white ring-1 ring-black/10 rounded-lg shadow-sm overflow-hidden">
                <div class="bg-gray-50 border-b border-gray-200 px-6 py-4">
                    <div class="flex items-center space-x-4">
                        <div class="flex items-center space-x-2 text-sm text-gray-600">
                            <span>{{ dayjs(report.created_at).format('MMM D, YYYY HH:mm') }}</span>
                            <template v-if="report.user">
                                <span class="text-gray-300">·</span>
                                <span>{{ report.user.name }}</span>
                                <BadgeUserRole v-if="report.user.role" :role="report.user.role" minimal />
                            </template>
                        </div>
                        <UBadge color="orange" label="Feedback" variant="solid" />
                    </div>
                </div>
                <div class="px-6 py-5">
                    <Field label="Notes">
                        <p class="whitespace-pre-line">{{ report.notes }}</p>
                    </Field>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import type { InterventionReport } from '~/types/fieldPost'
import type { UserRole } from '~/types/user'

type Report = Omit<InterventionReport, 'user'> & { user?: { id: string; name: string; role?: UserRole } | null }

const props = defineProps<{ interventionId: string }>()

const client = useSupabaseClient()

const reports = ref<Report[]>([])

onMounted(() => fetchReports())

async function fetchReports() {
    const { data } = await client
        .from('intervention_reports')
        .select('*, user:users!intervention_reports_user_id_fkey (id, name, role)')
        .eq('intervention_id', props.interventionId)
        .order('created_at', { ascending: false })
    reports.value = (data ?? []) as Report[]
}

defineExpose({ fetchReports })
</script>
